import { useSearchParams } from 'react-router-dom';
import {
    useGetCalendarMeetingByTokenQuery,
    useRespondToCalendarMeetingInvitationMutation
} from '../../api/calendar.js';
import React, { useEffect, useMemo, useState } from 'react';
import BaseButton from '../../components/form/BaseButton.jsx';
import ThreeDotsLoader from '../../components/misc/ThreeDotsLoader.jsx';
import dayjs from 'dayjs';
import Header from '../../components/layout/Header.jsx';
import Footer from '../../components/layout/Footer.jsx';
import DateTimeProposal from '../../components/misc/DateTimeProposal.jsx';
import IconPeople from '../../components/images/IconPeople.jsx';
import IconLocation from '../../components/images/IconLocation.jsx';
import IconOrganize from '../../components/images/IconOrganize.jsx';
import { useInfoToast } from '../../hooks/useToast.jsx';

const MeetingInvitation = () => {
    const [searchParams] = useSearchParams();
    const meetingToken = searchParams.get('meetingToken');
    const contactToken = searchParams.get('contactToken');
    const [propose, setPropose] = useState(false);
    const [reRender, setReRender] = useState(false);
    const [isConfirming, setIsConfirming] = useState(false);
    const [isDeclining, setIsDeclining] = useState(false);
    const [isProposing, setIsProposing] = useState(false);

    if (meetingToken === null || contactToken === null) return null;

    const {
        data: meetingResponse,
        isLoading,
        isFetching,
        refetch
    } = useGetCalendarMeetingByTokenQuery({ meetingToken, contactToken });
    const [respondToInvitation, { isLoading: isLoadingRespond, error }] =
        useRespondToCalendarMeetingInvitationMutation();

    const calendarMeeting = useMemo(() => {
        if (meetingResponse?.response?.meeting) {
            return meetingResponse.response.meeting;
        }

        return null;
    }, [meetingResponse]);

    const handleRespond = async (name, value = null) => {
        try {
            if (name === 'accepted') setIsConfirming(true);
            if (name === 'declined') setIsDeclining(true);
            if (name === 'propose') setIsProposing(true);

            switch (name) {
                case 'propose':
                    setPropose(true);
                    break;
                case 'proposed-change':
                    await respondToInvitation({
                        meetingToken,
                        contactToken,
                        params: {
                            response: name,
                            proposed_start_date: value.proposed_start_date,
                            proposed_end_date: value.proposed_end_date
                        }
                    });
                    setPropose(false);
                    setReRender(true);
                    break;
                default:
                    await respondToInvitation({ meetingToken, contactToken, params: { response: name } });
                    setReRender(true);
                    break;
            }
        } catch (error) {
            console.error(`Error while responding to invitation (${name}):`, error);
        } finally {
            setIsConfirming(false);
            setIsDeclining(false);
            setIsProposing(false);
        }
    };

    useEffect(() => {
        if (reRender) {
            refetch();
            setReRender(false);
        }
    }, [reRender, refetch]);

    const getStartAndEndDateTime = () => {
        if (calendarMeeting) {
            let date = '';

            if (calendarMeeting.start_date) {
                if (calendarMeeting.start_date !== calendarMeeting.end_date) {
                    date += 'From ' + dayjs(new Date(calendarMeeting.start_date)).format('dddd, MMM DD, YYYY');
                    if (calendarMeeting.start_time) {
                        date += ' at ' + dayjs(new Date(calendarMeeting.start_time)).format('HH:mm');
                    }

                    if (calendarMeeting.end_date) {
                        date += '\nTo ' + dayjs(new Date(calendarMeeting.end_date)).format('dddd, MMM DD, YYYY');
                        if (calendarMeeting.end_time) {
                            date += ' at ' + dayjs(new Date(calendarMeeting.end_time)).format('HH:mm');
                        }
                    }
                } else {
                    date += dayjs(new Date(calendarMeeting.start_date)).format('dddd, MMM DD, YYYY');
                    if (calendarMeeting.start_time) {
                        date += ' from ' + dayjs(new Date(calendarMeeting.start_time)).format('HH:mm');
                    }
                    if (calendarMeeting.end_time) {
                        date += ' to ' + dayjs(new Date(calendarMeeting.end_time)).format('HH:mm');
                    }
                }
            }

            return date;
        }

        return null;
    };

    const getProposedStartAndEndDateTime = () => {
        if (calendarMeeting?.contact) {
            const { proposed_start_date, proposed_end_date } = calendarMeeting.contact;
            let date = '';

            const hasTime = (isoString) => {
                const dateObj = new Date(isoString);
                return dateObj.getUTCHours() !== 0 || dateObj.getUTCMinutes() !== 0 || dateObj.getUTCSeconds() !== 0;
            };

            if (proposed_start_date) {
                date += dayjs(proposed_start_date).format('dddd, MMM DD, YYYY');
            }

            if (proposed_start_date === proposed_end_date) {
                if (proposed_start_date && hasTime(proposed_start_date)) {
                    date += '\nFrom ' + dayjs(proposed_start_date).format('HH:mm');
                }
                if (proposed_end_date && hasTime(proposed_end_date)) {
                    date += ' to ' + dayjs(proposed_end_date).format('HH:mm');
                }
            } else {
                if (proposed_start_date) {
                    if (hasTime(proposed_start_date)) {
                        date = 'From ' + date + ' at ' + dayjs(proposed_start_date).format('HH:mm');
                    } else {
                        date = 'From ' + date;
                    }
                }

                if (proposed_end_date) {
                    date += '\nTo ' + dayjs(proposed_end_date).format('dddd, MMM DD, YYYY');
                    if (hasTime(proposed_end_date)) {
                        date += ' at ' + dayjs(proposed_end_date).format('HH:mm');
                    }
                }
            }

            return date;
        }

        return null;
    };

    const isValidUrl = (string) => {
        try {
            return Boolean(new URL(string));
        } catch (e) {
            return false;
        }
    };

    const handleCopy = (text) => {
        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard
                .writeText(text)
                .then(() => useInfoToast('Copied to clipboard!'))
                .catch((err) => console.error('Clipboard copy failed', err));
        } else {
            try {
                document.execCommand('copy');
                useInfoToast('Copied link to clipboard!');
            } catch (err) {
                console.error('Fallback copy failed', err);
            }
        }
    };

    const renderResponses = () => {
        let text = '';
        switch (calendarMeeting.contact.meeting_invitation) {
            case 'accepted':
                text = 'Thank you for accepting to participate in the meeting!';
                break;
            case 'declined':
                text = 'We are sorry not to see you in attendance!';
                break;
            case 'proposed-change':
                text = 'You have proposed a new date/time: ';
                break;
        }

        return (
            <div className={'mb-5'}>
                <div className={'text-theme1-title2 font-bold text-lg'}>{text}</div>
                {calendarMeeting.contact.meeting_invitation === 'proposed-change' && (
                    <div className='text-theme1-title2 font-semibold flex items-center'>
                        <div className='text-theme1-text2 whitespace-pre-wrap'>{getProposedStartAndEndDateTime()}</div>
                    </div>
                )}
            </div>
        );
    };

    if (!calendarMeeting) return null;

    return (
        <>
            <div className='h-screen flex flex-col relative'>
                <Header title={'Meeting Invitation'} goBack={false} sidebar={false} />
                {isLoading || isFetching ? (
                    <>
                        <div className='text-center mt-[100px]'>
                            <ThreeDotsLoader />
                            <div className='text-theme1-primary font-bold text-xl mt-[10px]'>Loading...</div>
                        </div>
                    </>
                ) : (
                    <>
                        <div className='flex flex-col max-w-3xl h-full w-full mx-auto px-8'>
                            <div className='flex flex-col max-w-3xl w-full mx-auto'>
                                <div className='flex pt-8'>
                                    <div className='font-bold text-3xl text-theme1-primary'>
                                        Hello, {calendarMeeting.contact.contact.first_name}!
                                    </div>
                                </div>
                                <div className='text-theme1-primary2'>Please check the details of your meeting.</div>
                            </div>
                            <div className='mt-8'>
                                <div className='flex gap-4 items-center mb-6'>
                                    <div className='bg-theme1-gold rounded-xl flex items-center justify-center w-[44px] h-[44px]'>
                                        <IconPeople className='stroke-white fill-transparent w-6 h-6' />
                                    </div>
                                    <div className='text-theme1-title2 font-bold text-[22px]'>
                                        {calendarMeeting.title}
                                    </div>
                                </div>
                                <div className='flex gap-4 items-center mb-6'>
                                    <div className='bg-theme1-gold rounded-xl flex items-center justify-center w-[44px] h-[44px]'>
                                        <IconOrganize className='stroke-white fill-transparent w-6 h-6' />
                                    </div>
                                    <div className='text-theme1-title2 font-bold text-xl'>
                                        <div>Time</div>

                                        <div className='text-sm font-normal text-theme1-text2'>
                                            {getStartAndEndDateTime()}
                                        </div>
                                    </div>
                                </div>
                                {calendarMeeting?.is_recurrent && (
                                    <div className='-mt-3'>
                                        <div className='flex items-center mb-2 gap-x-2 flex-wrap'>
                                            <div className='text-theme1-title2 font-semibold'>Meeting repeats:</div>
                                            <div className='text-theme1-title2 capitalize'>
                                                {calendarMeeting.frequency}
                                            </div>
                                        </div>
                                        {(calendarMeeting.frequency === 'weekly' ||
                                            calendarMeeting.frequency === 'monthly') && (
                                            <div className='flex items-center mb-2 gap-x-2 flex-wrap'>
                                                <div className='text-theme1-title2 font-semibold'>
                                                    Meeting repeats on:
                                                </div>
                                                {calendarMeeting.frequency === 'weekly' && (
                                                    <div className='text-theme1-title2 capitalize'>
                                                        {calendarMeeting.week_days.map((day) => day).join(', ')}
                                                    </div>
                                                )}
                                                {calendarMeeting.frequency === 'monthly' && (
                                                    <div className='text-theme1-title2 capitalize'>
                                                        {calendarMeeting.month_days.map((day) => day).join(', ')}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                        {calendarMeeting.recurrence_end_type !== 'never' && (
                                            <div className='flex items-center mb-2 gap-x-2 flex-wrap'>
                                                <div className='text-theme1-title2 font-semibold'>
                                                    Recurrence ends on:
                                                </div>
                                                <div className='text-theme1-title2 capitalize'>
                                                    {dayjs(calendarMeeting.recurrence_end_date).format(
                                                        'dddd, MMM D, YYYY [at] HH:mm'
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                                {(calendarMeeting.location || calendarMeeting.country_id) &&
                                    (calendarMeeting.location && isValidUrl(calendarMeeting.location) ? (
                                        <div className='card flex justify-between align-items-center gap-3'>
                                            <div className='flex gap-4 items-center mb-6'>
                                                <div className='bg-theme1-gold rounded-xl flex items-center justify-center w-[44px] h-[44px]'>
                                                    <IconLocation className='fill-white w-6 h-6' />
                                                </div>
                                                <div className='text-theme1-title2 font-bold text-xl'>
                                                    <div>Online Meeting</div>
                                                </div>
                                            </div>
                                            <div>
                                                <BaseButton
                                                    label='Link'
                                                    className='text-theme1-primary bg-theme1-pinkPrimary w-50 p-3 mr-[10px]'
                                                    textValue={'Join'}
                                                    link
                                                    onClick={() => window.open(calendarMeeting.location, '_blank')}
                                                />
                                                <BaseButton
                                                    label='Link'
                                                    className='text-theme1-primary bg-theme1-pinkPrimary w-50 p-3'
                                                    textValue={
                                                        <svg
                                                            width='14'
                                                            height='18'
                                                            viewBox='0 0 14 18'
                                                            fill='none'
                                                            xmlns='http://www.w3.org/2000/svg'
                                                        >
                                                            <svg
                                                                width='14'
                                                                height='18'
                                                                viewBox='0 0 14 18'
                                                                fill='none'
                                                                xmlns='http://www.w3.org/2000/svg'
                                                            >
                                                                <svg
                                                                    width='14'
                                                                    height='18'
                                                                    viewBox='0 0 14 18'
                                                                    fill='none'
                                                                    xmlns='http://www.w3.org/2000/svg'
                                                                >
                                                                    <path
                                                                        d='M2.77393 17.5304C1.96338 17.5304 1.3457 17.3205 0.920898 16.9006C0.500977 16.4806 0.291016 15.8703 0.291016 15.0695V7.93571C0.291016 7.13004 0.500977 6.51969 0.920898 6.10465C1.3457 5.68473 1.96338 5.47477 2.77393 5.47477H4.86133V7.10075H2.90576C2.5835 7.10075 2.33691 7.18375 2.16602 7.34977C2 7.5109 1.91699 7.75993 1.91699 8.09684V14.901C1.91699 15.238 2 15.487 2.16602 15.6481C2.33691 15.8141 2.5835 15.8971 2.90576 15.8971H11.0869C11.4043 15.8971 11.6484 15.8141 11.8193 15.6481C11.9902 15.487 12.0757 15.238 12.0757 14.901V8.09684C12.0757 7.75993 11.9902 7.5109 11.8193 7.34977C11.6484 7.18375 11.4043 7.10075 11.0869 7.10075H9.13135V5.47477H11.2261C12.0366 5.47477 12.6519 5.68473 13.0718 6.10465C13.4966 6.51969 13.709 7.13004 13.709 7.93571V15.0695C13.709 15.8703 13.4966 16.4806 13.0718 16.9006C12.6519 17.3205 12.0366 17.5304 11.2261 17.5304H2.77393ZM6.99268 11.5026C6.7876 11.5026 6.61182 11.4294 6.46533 11.2829C6.31885 11.1364 6.24561 10.9655 6.24561 10.7702V3.48258L6.31152 2.40592L5.94531 2.89664L4.96387 3.94401C4.82715 4.08561 4.65869 4.15641 4.4585 4.15641C4.28271 4.15641 4.12891 4.09782 3.99707 3.98063C3.87012 3.86344 3.80664 3.71207 3.80664 3.52653C3.80664 3.35075 3.875 3.19205 4.01172 3.05045L6.41406 0.743324C6.5166 0.645668 6.61426 0.57975 6.70703 0.54557C6.7998 0.506508 6.89502 0.486977 6.99268 0.486977C7.09521 0.486977 7.19287 0.506508 7.28564 0.54557C7.3833 0.57975 7.48096 0.645668 7.57861 0.743324L9.98096 3.05045C10.1177 3.19205 10.186 3.35075 10.186 3.52653C10.186 3.71207 10.1201 3.86344 9.98828 3.98063C9.85645 4.09782 9.70264 4.15641 9.52686 4.15641C9.32666 4.15641 9.16064 4.08561 9.02881 3.94401L8.05469 2.89664L7.68848 2.40592L7.74707 3.48258V10.7702C7.74707 10.9655 7.67383 11.1364 7.52734 11.2829C7.38574 11.4294 7.20752 11.5026 6.99268 11.5026Z'
                                                                        fill='#921515'
                                                                    />
                                                                </svg>
                                                            </svg>
                                                        </svg>
                                                    }
                                                    link
                                                    onClick={() => handleCopy(calendarMeeting.location)}
                                                />
                                            </div>
                                        </div>
                                    ) : (
                                        <div className='flex gap-4 items-center mb-6'>
                                            <div className='bg-theme1-gold rounded-xl flex items-center justify-center w-[44px] h-[44px]'>
                                                <IconLocation className='fill-white w-6 h-6' />
                                            </div>
                                            <div className='text-theme1-title2 font-bold text-xl'>
                                                <div>Location</div>

                                                <div className='text-sm font-normal text-theme1-text2'>
                                                    {calendarMeeting.country_id
                                                        ? `${calendarMeeting.country.code}${calendarMeeting.location ? `, ${calendarMeeting.location}` : ''}`
                                                        : calendarMeeting.location}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                            </div>
                        </div>
                        {'meeting_invitation' in calendarMeeting.contact && (
                            <div className='flex flex-col max-w-3xl h-full w-full mx-auto px-8 mb-10'>
                                <div className='flex pt-8'>
                                    <div className={'text-theme1-title2'}>
                                        {renderResponses()} If you'd like to change your response, click one of the
                                        buttons bellow!
                                    </div>
                                </div>
                            </div>
                        )}
                        <div>
                            <div className='flex flex-col max-w-3xl w-full mx-auto px-4 mb-6'>
                                <div>
                                    <div className='flex flex-col justify-center items-center'>
                                        <BaseButton
                                            name={'confirm'}
                                            onClick={() => handleRespond('accepted')}
                                            className={`${calendarMeeting.contact.meeting_invitation === 'accepted' ? 'btn-primary' : 'btn-primary-outline'}`}
                                            textValue={isConfirming ? 'Confirming...' : 'Confirm'}
                                            disabled={
                                                calendarMeeting.contact.meeting_invitation === 'accepted' ||
                                                isConfirming
                                            }
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className='flex flex-col max-w-3xl w-full mx-auto px-4 mb-6'>
                                <div>
                                    <div className='flex flex-col justify-center items-center'>
                                        <BaseButton
                                            name={'decline'}
                                            onClick={() => handleRespond('declined')}
                                            className={`${calendarMeeting.contact.meeting_invitation === 'declined' ? 'btn-primary' : 'btn-primary-outline'}`}
                                            textValue={isDeclining ? 'Declining...' : 'Decline'}
                                            disabled={
                                                calendarMeeting.contact.meeting_invitation === 'declined' || isDeclining
                                            }
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className='flex flex-col max-w-3xl w-full mx-auto px-4 mb-6'>
                                <div>
                                    <div className='flex flex-col justify-center items-center'>
                                        <BaseButton
                                            name={'propose'}
                                            onClick={() => handleRespond('propose')}
                                            className={`${calendarMeeting.contact.meeting_invitation === 'proposed-change' ? 'btn-primary' : 'btn-primary-outline'}`}
                                            textValue={isProposing ? 'Proposing...' : 'Propose new date/time'}
                                            disabled={
                                                calendarMeeting.contact.meeting_invitation === 'proposed-change' ||
                                                isProposing
                                            }
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <Footer />
                    </>
                )}
                <DateTimeProposal
                    setVisible={setPropose}
                    visible={propose}
                    isLoading={isLoadingRespond}
                    handleRespond={handleRespond}
                    proposedValues={calendarMeeting?.contact}
                />
            </div>
        </>
    );
};

export default MeetingInvitation;
